/**
 * Shared form field utilities — single source of truth for field flattening.
 *
 * Previously duplicated across 6 files with inconsistent behavior:
 * - FormDataPage.jsx (one-level flatten, mirror-first)
 * - FormBuilder.jsx (recursive, one step)
 * - forms.py _flatten_fields (recursive, one source)
 * - forms.py _get_all_fields_from_steps (recursive, all steps)
 * - ConditionEditor.jsx allFields (no recursion, all steps)
 * - PublicForm.jsx flattenFields (recursive, one step)
 *
 * This module provides the canonical recursive flattener and step union.
 */

import { STRUCTURAL_TYPES } from './formConstants';

/**
 * Recursively flatten a field tree, descending into container.children.
 * Returns ALL fields (including structural types like containers, dividers, etc.)
 * — callers that need only input fields should use flattenInputFields().
 *
 * @param {Array} nodes - field tree (e.g. step.fields)
 * @returns {Array} flat list of all field nodes
 */
export function flattenFields(nodes) {
    const flat = [];
    for (const n of (nodes || [])) {
        flat.push(n);
        if (n.field_type === 'container' && n.children) {
            flat.push(...flattenFields(n.children));
        }
    }
    return flat;
}

/**
 * Recursively flatten a field tree and return ONLY input fields
 * (excludes structural types: container, section_heading, divider, spacer, html_block).
 *
 * @param {Array} nodes - field tree (e.g. step.fields)
 * @returns {Array} flat list of input-only field nodes
 */
export function flattenInputFields(nodes) {
    return flattenFields(nodes).filter(
        f => !STRUCTURAL_TYPES.includes(f.field_type)
    );
}

/**
 * Get all input fields from all question steps (non-router), deduped by field_id.
 * Steps with a `branch` object are router steps and have no fields to contribute.
 *
 * @param {Array} steps - form steps
 * @returns {Array} flat list of unique input fields across all question steps
 */
export function getAllFieldsFromSteps(steps) {
    const seen = new Set();
    const result = [];
    for (const step of (steps || [])) {
        if (step.kind !== 'question') continue;
        if (step.branch) continue; // router steps have no fields
        const fields = flattenInputFields(step.fields || []);
        for (const f of fields) {
            if (f.field_id && !seen.has(f.field_id)) {
                seen.add(f.field_id);
                result.push(f);
            }
        }
    }
    return result;
}

/**
 * The public URL of a form ("https://app.example.com/form/summit-signup"). One builder for the Forms list's Copy URL and the
 * event's Registration forms card (#919), so the two can never disagree.
 */
export function publicFormUrl(slug) {
    return `${window.location.origin}/form/${slug}`;
}
